/**
 * Widget météo du jour (accueil) — alpha.
 *
 * Source : **Open-Meteo** (open-meteo.com) — API météo gratuite, sans
 * clé ni compte, sans traçage publicitaire. On n'envoie une position
 * (arrondie à 2 décimales ≈ 1 km, jamais la position exacte) QUE
 * lorsque l'utilisateur active explicitement la météo (geste sur le
 * bouton → invite de géolocalisation du navigateur). Rien n'est
 * transmis sans ce geste.
 *
 * Local-only §2.4 : la dernière météo connue (position comprise) est
 * cachée en `localStorage`. Au boot, le widget la ré-affiche
 * instantanément (hors-ligne compris) puis rafraîchit en tâche de
 * fond si elle date de plus de 30 min. Une panne réseau ne bloque
 * jamais le rendu de l'accueil — le widget garde la dernière valeur.
 *
 * Le module est **pur et testable** : `wmoToCondition`, `fetchWeather`
 * (fetch injectable) et le cache sont couverts par `weather.test.ts`.
 * L'UI (bouton d'activation, carte) vit dans `home/View.ts`.
 */
import { createStore } from '../../lib/store.js';
import { getLang } from '../../i18n/index.js';
import type { TKey } from '../../i18n/index.js';
import type { IconName } from '../../ui/icons.js';

/** Famille de conditions (mappe les codes WMO d'Open-Meteo). */
export type WeatherCondition =
  | 'clear'
  | 'partly'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'snow'
  | 'thunder';

/** Instantané météo normalisé (ce qu'on cache et affiche). */
export interface WeatherNow {
  /** Température courante (°C, arrondie). */
  temp: number;
  /** Maximum du jour (°C). */
  hi: number;
  /** Minimum du jour (°C). */
  lo: number;
  condition: WeatherCondition;
  /** Code WMO brut (diagnostic). */
  code: number;
  /** Horodatage du fetch (ms epoch). */
  at: number;
  /** Position arrondie utilisée (pour le rafraîchissement). */
  lat: number;
  lon: number;
  /** Ville résolue (feedback alpha, item 8) — best-effort, optionnelle. */
  city?: string;
}

// ── Mapping WMO → condition ───────────────────────────────────────────

/**
 * Traduit un code météo WMO (World Meteorological Organization, tel
 * que renvoyé par Open-Meteo) vers une famille de conditions. Codes :
 * 0 clair · 1-2 éclaircies · 3 couvert · 45/48 brouillard ·
 * 51-57 bruine · 61-67 & 80-82 pluie · 71-77 & 85-86 neige ·
 * 95-99 orage.
 */
export function wmoToCondition(code: number): WeatherCondition {
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'thunder';
  return 'cloudy'; // fallback prudent (codes inconnus → neutre)
}

// Audit D2 (zéro emoji UI) : le widget rend des icônes filaires — on
// mappe chaque condition vers un nom d'icône du design system.
const CONDITION_ICON: Record<WeatherCondition, IconName> = {
  clear: 'sun',
  partly: 'cloudSun',
  cloudy: 'cloud',
  fog: 'fog',
  drizzle: 'drizzle',
  rain: 'rain',
  snow: 'snow',
  thunder: 'storm',
};

/** Nom d'icône filaire d'une condition (rendue via `Icon(Icons[...])`). */
export function conditionIconName(c: WeatherCondition): IconName {
  return CONDITION_ICON[c];
}

/** Clé i18n du libellé d'une condition (`weather_cond_*`). */
export function conditionLabelKey(c: WeatherCondition): TKey {
  return `weather_cond_${c}` as TKey;
}

// ── Reverse geocoding (ville) ─────────────────────────────────────────

/**
 * Résout le nom de la ville depuis la position ARRONDIE (~1 km) —
 * feedback alpha (item 8). Service : BigDataCloud reverse-geocode
 * « client » (gratuit, sans clé, prévu pour l'appel navigateur ;
 * amendement ADR 0013). Best-effort : toute erreur → pas de ville,
 * le widget fonctionne sans.
 */
export async function resolveCity(
  lat: number,
  lon: number,
  fetchImpl: typeof fetch = fetch,
): Promise<string | undefined> {
  try {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${lat.toString()}&longitude=${lon.toString()}` +
      `&localityLanguage=${getLang()}`;
    const res = await fetchImpl(url);
    if (!res.ok) return undefined;
    const j = (await res.json()) as { city?: string; locality?: string };
    const city = (j.city ?? '').trim() || (j.locality ?? '').trim();
    return city || undefined;
  } catch {
    return undefined;
  }
}

// ── Fetch Open-Meteo ──────────────────────────────────────────────────

const ENDPOINT = 'https://api.open-meteo.com/v1/forecast';

interface OpenMeteoResponse {
  current?: { temperature_2m?: number; weather_code?: number };
  daily?: { temperature_2m_max?: number[]; temperature_2m_min?: number[] };
}

/**
 * Interroge Open-Meteo pour la position donnée. `fetchImpl` est
 * injectable (aucun réseau réel en test). Throw sur réponse non-2xx
 * ou payload incomplet — l'appelant retombe sur le cache.
 */
export async function fetchWeather(
  lat: number,
  lon: number,
  fetchImpl: typeof fetch = fetch,
): Promise<WeatherNow> {
  const rlat = Math.round(lat * 100) / 100;
  const rlon = Math.round(lon * 100) / 100;
  const url =
    `${ENDPOINT}?latitude=${rlat.toString()}&longitude=${rlon.toString()}` +
    `&current=temperature_2m,weather_code` +
    `&daily=temperature_2m_max,temperature_2m_min` +
    `&timezone=auto&forecast_days=1`;
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`HTTP ${res.status.toString()}`);
  const j = (await res.json()) as OpenMeteoResponse;
  const temp = j.current?.temperature_2m;
  const code = j.current?.weather_code;
  const hi = j.daily?.temperature_2m_max?.[0];
  const lo = j.daily?.temperature_2m_min?.[0];
  if (temp === undefined || code === undefined || hi === undefined || lo === undefined) {
    throw new Error('incomplete weather payload');
  }
  return {
    temp: Math.round(temp),
    hi: Math.round(hi),
    lo: Math.round(lo),
    condition: wmoToCondition(code),
    code,
    at: Date.now(),
    lat: rlat,
    lon: rlon,
  };
}

// ── Store réactif + cache localStorage ────────────────────────────────

/**
 * `idle` : jamais activé, aucun cache → on montre le bouton
 * « Activer la météo ». `locating`/`loading` : en cours (garde la
 * carte si `data` présent). `ready` : à jour. `error` : dernier
 * fetch échoué mais on garde le cache. `denied` : géoloc refusée.
 */
export type WeatherStatus = 'idle' | 'locating' | 'loading' | 'ready' | 'error' | 'denied';

export interface WeatherState {
  status: WeatherStatus;
  /** Dernière météo connue (cache), conservée même en erreur. */
  data: WeatherNow | null;
}

const CACHE_KEY = 'nestor_weather';
const TTL_MS = 30 * 60 * 1000; // 30 min : au-delà, on rafraîchit.

const store = createStore<WeatherState>({ status: 'idle', data: null });

export function getWeatherState(): WeatherState {
  return store.getState();
}

/** S'abonne aux changements (l'AppShell re-render la vue courante). */
export function subscribeWeather(cb: () => void): () => void {
  return store.subscribe(() => {
    cb();
  });
}

function readCache(): WeatherNow | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const w = JSON.parse(raw) as WeatherNow;
    // Validation minimale : un cache corrompu ne doit pas casser l'accueil.
    if (typeof w.temp !== 'number' || typeof w.lat !== 'number' || typeof w.at !== 'number') {
      return null;
    }
    return w;
  } catch {
    return null;
  }
}

function writeCache(w: WeatherNow): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(w));
  } catch {
    // Storage plein / indisponible : le widget marche quand même en mémoire.
  }
}

/**
 * Hydrate le widget depuis le cache au boot. Si le cache est frais
 * (< 30 min) on l'affiche tel quel ; s'il est périmé on l'affiche
 * quand même puis on rafraîchit en tâche de fond. Sans cache : `idle`
 * (bouton d'activation). Fire-and-forget, à appeler une fois au boot.
 */
export function initWeather(fetchImpl: typeof fetch = fetch): void {
  const cached = readCache();
  if (!cached) return; // reste idle
  store.setState({ status: 'ready', data: cached });
  if (Date.now() - cached.at > TTL_MS) {
    void refreshWeather(fetchImpl);
  }
}

/** Rafraîchit avec la dernière position connue (no-op sans position). */
export async function refreshWeather(fetchImpl: typeof fetch = fetch): Promise<void> {
  const cached = store.getState().data ?? readCache();
  if (!cached) return;
  store.setState({ status: 'loading', data: cached });
  try {
    const w = await fetchWeather(cached.lat, cached.lon, fetchImpl);
    // La position n'a pas changé : la ville du cache reste valable.
    const full: WeatherNow = { ...w, ...(cached.city ? { city: cached.city } : {}) };
    writeCache(full);
    store.setState({ status: 'ready', data: full });
  } catch {
    store.setState({ status: 'error', data: cached });
  }
}

/**
 * Active la météo : demande la géolocalisation (invite navigateur),
 * puis interroge Open-Meteo. La position est mémorisée dans le cache
 * pour que les prochains boots n'aient plus à redemander. Refus de
 * géoloc → `denied` (message bienveillant, réactivable).
 */
export function enableWeather(fetchImpl: typeof fetch = fetch): void {
  const current = store.getState().data;
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    store.setState({ status: 'error', data: current });
    return;
  }
  store.setState({ status: 'locating', data: current });
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      void (async () => {
        store.setState({ status: 'loading', data: store.getState().data });
        try {
          const [w, city] = await Promise.all([
            fetchWeather(pos.coords.latitude, pos.coords.longitude, fetchImpl),
            resolveCity(
              Math.round(pos.coords.latitude * 100) / 100,
              Math.round(pos.coords.longitude * 100) / 100,
              fetchImpl,
            ),
          ]);
          const full: WeatherNow = { ...w, ...(city ? { city } : {}) };
          writeCache(full);
          store.setState({ status: 'ready', data: full });
        } catch {
          store.setState({ status: 'error', data: store.getState().data });
        }
      })();
    },
    () => {
      store.setState({ status: 'denied', data: store.getState().data });
    },
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 },
  );
}

/** @internal pour tests. */
export function _resetWeatherForTests(): void {
  store.setState({ status: 'idle', data: null }, true);
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
